import React, { useState } from "react";
import { getIcon, isNotEmpty } from "../../utils/generalUtils.js";
import { detailsKeywords } from "../../config/constant.js";
import { RenderDynamicContent } from "./RenderDynamicContent.jsx";
import { HalfCircularGauge } from "./HalfGauge.jsx";

function isNullOrAllNull(value) {
  if (value === null) return true;
  if (Array.isArray(value)) {
    return value.length === 0 || value.every(isNullOrAllNull);
  }
  if (typeof value === "object") {
    return (
      Object.keys(value).length === 0 ||
      Object.values(value).every(isNullOrAllNull)
    );
  }
  return false;
}

function DetailSection({ sections, selectedApplicationIdInfo }) {
  const [activeTab, setActiveTab] = useState(0);

  if (!sections || sections.length === 0) {
    return (
      <div className="p-4 bg-white rounded-lg text-center text-gray-500 italic">
        No sections available.
      </div>
    );
  }

  const activeKey = sections[activeTab];
  const activeSection = selectedApplicationIdInfo?.[activeKey];
  const links = activeSection?.links || [];

  return (
    <div className="w-full lg:w-2/3 xl:w-3/4 space-y-2">
      {/* Score Highlights */}
      <div className="p-4 bg-white rounded-lg">
        <h2 className="text-lg font-semibold mb-2">Score Highlights</h2>
        <div className="grid grid-cols-4 lg:grid-cols-7 gap-2">
          {sections && sections
            .filter((section) =>
              detailsKeywords.some((keyword) =>
                section.toLowerCase().includes(keyword)
              )
            )
            .map((label, idx) => (
              <div
                key={idx}
                style={{ aspectRatio: "1 / 1" }}
                className="p-2 flex flex-col items-center border border-gray-200 rounded-lg text-center cursor-pointer gap-1"
                onClick={() => setActiveTab(idx)}
              >
                <span className="text-sm font-semibold text-gray-500 capitalize">
                  {(() => {
                    const words = label?.replace(/_/g, " ").split(" ");
                    if (words.length > 1) {
                      return (
                        <>
                          {words[0]}
                          <br />
                          {words.slice(1).join(" ")}
                        </>
                      );
                    }
                    return words[0];
                  })()}
                </span>
                <HalfCircularGauge
                  value={selectedApplicationIdInfo[label]?.score}
                  max={10}
                  gradientFrom={
                    selectedApplicationIdInfo[label]?.score >= 7
                      ? "#04CD18"
                      : selectedApplicationIdInfo[label]?.score >= 4
                        ? "#FF9500"
                        : "#FF3B30"
                  }
                  gradientTo={
                    selectedApplicationIdInfo[label]?.score >= 7
                      ? "#A6FF98"
                      : selectedApplicationIdInfo[label]?.score >= 4
                        ? "#FFD292"
                        : "#FFD1CF"
                  }
                />
              </div>
            ))}
        </div>
      </div>

      {/* Tabbed Content */}
      <div className="p-4 space-y-4 bg-white rounded-lg">
        <div className="flex overflow-x-auto  font-semibold p-1 bg-gray-100 rounded-full scrollbar-hide">
          {sections.map((tab, idx) => (
            <div
              key={idx}
              className={`px-3 py-2 whitespace-nowrap  font-semibold rounded-full cursor-pointer capitalize ${idx === activeTab
                ? "bg-orange-600 text-white"
                : "bg-gray-100 text-gray-400"
                }`}
              onClick={() => setActiveTab(idx)}
            >
              {tab?.replace(/_/g, " ")}
            </div>
          ))}
        </div>

        {/* Active Content */}
        <div className="space-y-4 ">
          <div className="flex items-center justify-between">
            <h1 className="text-base font-semibold underline capitalize">
              {activeKey?.replace(/_/g, " ")}
            </h1>
            {activeSection?.score != null && (
              <div
                className={`p-2 bg-opacity-10 rounded-lg ${activeSection?.score >= 7
                  ? "bg-green-600"
                  : activeSection?.score >= 4
                    ? "bg-orange-500"
                    : "bg-red-600"
                  }`}
              >
                <span>{getIcon(activeSection?.score)}</span>
                <span className="font-bold ml-1">
                  Score: {activeSection?.score}/10
                </span>
              </div>
            )}
          </div>

          {isNullOrAllNull(activeSection) ? (
            <div className="px-3 pb-3 rounded-lg text-gray-800">
              <p className="text-gray-500 italic">No data available</p>
            </div>
          ) : (
            <div className="space-y-2 overflow-y-auto max-h-[400px] pr-2">
              {Object.entries(activeSection).map(([subKey, value]) => {
                if (subKey === "score" || subKey === "company_name") return null;
                return (
                  isNotEmpty(value) && (
                    <div key={`${activeKey}-${subKey}`}>
                      <h3 className="font-semibold text-gray-700 capitalize">
                        {subKey?.replace(/_/g, " ")}
                      </h3>
                      <div className="px-3 pb-3 rounded-lg text-gray-800">
                        {RenderDynamicContent(value, links)}
                      </div>
                    </div>
                  )
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* References */}
      {links.length > 0 && (
        <div className="p-4 bg-white rounded-lg">
          <h4 className="text-md font-semibold mb-2">References</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 ">
            {links.map((link, i) => (
              <div key={i} className="p-2 bg-gray-50 border rounded-md">
                <p className="font-medium text-gray-800">{link.title}</p>
                <p className="text-gray-600 text-sm">
                  <a
                    href={link.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline"
                  >
                    {link.title}
                  </a>
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default DetailSection;
